import { useState } from "react";
import { Lock, Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

export default function ChangePasswordModal({ onComplete }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters");
    if (newPassword !== confirmPassword) return toast.error("Passwords don't match");

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword }, { withCredentials: true });
      toast.success("Password updated!");
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-sm mx-4 rounded-2xl border border-white/[0.08] bg-[#0F0F0F] p-6" data-testid="change-password-modal">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 bg-[#f59e0b]/10">
            <ShieldCheck size={22} weight="bold" className="text-[#f59e0b]" />
          </div>
          <h2 className="text-lg font-bold text-white">Set Your Password</h2>
          <p className="text-xs text-[#71717A] mt-1">Your admin created your account with a temporary password. Please set a new one.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-[#A1A1AA] mb-1.5 uppercase tracking-wider">New Password</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
              <input type={showPass ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 6 characters"
                className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-[#71717A] outline-none focus:border-[#9D32F3]/50 transition-all"
                data-testid="new-password" autoFocus />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A93] hover:text-[#A1A1AA] transition-all" tabIndex={-1}>
                {showPass ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-[#A1A1AA] mb-1.5 uppercase tracking-wider">Confirm Password</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
              <input type={showPass ? "text" : "password"} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-[#71717A] outline-none focus:border-[#9D32F3]/50 transition-all"
                data-testid="confirm-password" />
            </div>
          </div>

          <button type="submit" disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword} data-testid="change-password-submit"
            className="w-full py-2.5 text-sm font-bold text-white rounded-xl transition-all disabled:opacity-40 hover:shadow-[0_0_25px_rgba(157,50,243,0.3)]"
            style={{ background: "linear-gradient(135deg, #9D32F3, #3B40D4)" }}>
            {loading ? "Updating..." : "Set Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
